import { z } from 'zod';

import type { Org } from '@airreading/common/data';
import type { School } from '@airreading/common/data';
import type { UserLike } from '@airreading/common/data';

export const entityTypeSchema = z.enum(['org', 'school', 'user', 'user_role']);
/**
 * The types of entities that can be associated with an external service,
 * usually for syncing purposes.
 */
export type EntityType = z.infer<typeof entityTypeSchema>;
export const EntityType = entityTypeSchema.enum;

/**
 * An entity that contains runtime information on what type of entity it is.
 * This makes it easier to associate it with an ID from an external service by
 * using metadata join tables that differentiate by entity type.
 *
 * Because of this convention, when defining a new data service type, please
 * avoid using the property name `type` unless you explicitly intend to opt into
 * the protocol described here. The suggested alternative is `kind`.
 */
export interface AssociatableEntity {
  type: EntityType;
  id: string;
}

/**
 * The following block is intentional dead code. Due to the way our data types
 * are defined, we cannot check that they conform to some other interface at the
 * sites they are defined. Where we can normally do:
 *
 *     interface T extends U {}
 *
 * We instead must need a way to express:
 *
 *     assert type T extends U;
 *
 * A very compact way to actually accomplish this is:
 *
 *     ((_: U) => 0)(0 as any as T);
 *
 * Even though the above function compiles to `(() => 0)(0)`, which does
 * nothing, we can do even better by surrounding it with an `if (false)`. This
 * ensures none of that ever gets executed at runtime.
 */
if (false) {
  // Conformance checks: these types must conform to `AssociatableEntity`.
  ((_: AssociatableEntity) => 0)(0 as any as Org);
  ((_: AssociatableEntity) => 0)(0 as any as School);
  ((_: AssociatableEntity) => 0)(0 as any as UserLike);
}
